"use client"

import { cn } from "@heroui/react"
import { useId } from "react"
import { useTranslations } from "next-intl"
import { getModelBestOff, isDiscountOff, type AssetModel } from "@/apis/asset"

/** 模型下拉选项（图标 / 名称 / 介绍 / 剩余免费次数） */
export type ModelOption = {
  key: string
  name: string
  desc: string
  icon?: string
  /** 模型剩余免费次数：所有分辨率、所有时长档位之和 */
  free: number
  /** 热门/推荐：名称后展示火花 */
  isFire?: boolean
  /** 折扣系数 0–100；0 = 无折扣 */
  off?: number
}

/** 接口模型 -> 下拉选项 */
export function toModelOption(model: AssetModel): ModelOption {
  const free = (model.video_support ?? []).reduce(
    (sum, v) =>
      sum +
      (v.scenes.find((s) => s.scene === "r2v")?.second_options ?? []).reduce(
        (s, o) => s + o.free_remaining,
        0,
      ),
    0,
  )
  return {
    key: String(model.id),
    name: model.name,
    desc: model.description,
    icon: model.ref_images?.[0],
    free,
    isFire: !!model.is_fire,
    // 外层取各分辨率中优惠力度最大的一档
    off: getModelBestOff(model),
  }
}

/** 限时折扣角标（模型列表 / 分辨率档位共用） */
export function OffBadge({ off, className }: { off?: number; className?: string }) {
  const t = useTranslations("generatePage")
  if (!isDiscountOff(off)) return null
  return (
    <span
      className={cn(
        "shrink-0 rounded bg-[linear-gradient(104deg,#FFFDED_0%,#FFF3C4_70%,#F892AC_100%)] px-1 text-[10px] font-medium capitalize leading-4 text-[#332314]",
        className,
      )}
    >
      {t("timeLimitedOff", { percent: 100 - off })}
    </span>
  )
}

/** 热门模型火花（名称后角标） */
export function FireGlyph({ className }: { className?: string }) {
  const gid = useId().replace(/:/g, "")
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <path
        d="M13.0328 7.33142C12.7296 6.64874 12.2888 6.03596 11.738 5.53142L11.2831 5.11428C11.2677 5.1004 11.2491 5.09054 11.2289 5.08555C11.2088 5.08057 11.1878 5.08062 11.1677 5.08571C11.1475 5.09079 11.129 5.10075 11.1137 5.1147C11.0983 5.12865 11.0866 5.14617 11.0797 5.1657L10.8768 5.74856C10.7505 6.11428 10.5174 6.48742 10.1877 6.85485C10.171 6.87285 10.1481 6.88387 10.1237 6.8857C10.1114 6.88691 10.0991 6.8855 10.0875 6.88156C10.0759 6.87761 10.0652 6.87124 10.0562 6.86285C10.0456 6.85352 10.0373 6.84185 10.0319 6.82876C10.0266 6.81567 10.0243 6.80152 10.0254 6.78742C10.0825 5.84685 9.80195 4.7857 9.18595 3.63142C8.67681 2.67142 7.96881 1.92342 7.08481 1.40171L6.43909 1.02171C6.41961 1.01071 6.39755 1.0051 6.37518 1.00548C6.35281 1.00586 6.33094 1.01219 6.31184 1.02384C6.29274 1.03549 6.27709 1.05202 6.26651 1.07174C6.25593 1.09145 6.25081 1.11363 6.25167 1.13599L6.28595 1.88571C6.30938 2.39828 6.24995 2.85142 6.10938 3.22856C5.93678 3.69067 5.68865 4.1209 5.37509 4.5017C5.15338 4.77028 4.90481 5.01256 4.63281 5.2217C3.97399 5.72838 3.43833 6.37753 3.06595 7.12056C2.71823 7.82229 2.5263 8.59089 2.50332 9.37371C2.48034 10.1565 2.62685 10.9351 2.93281 11.656C3.49517 12.9662 4.54524 14.0062 5.86081 14.556C6.53816 14.8404 7.26562 14.9861 8.00024 14.9846C8.73447 14.9858 9.46159 14.8407 10.1391 14.5577C10.7922 14.286 11.3861 13.8897 11.8877 13.3908C12.3991 12.887 12.8051 12.2863 13.0819 11.6239C13.3588 10.9614 13.501 10.2505 13.5002 9.53256C13.5002 8.77028 13.3442 8.0297 13.0328 7.33142Z"
        fill={`url(#${gid})`}
      />
      <defs>
        <linearGradient id={gid} x1="2.50098" y1="2.45175" x2="13.5002" y2="13.5383" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFC744" />
          <stop offset="1" stopColor="#FF4486" />
        </linearGradient>
      </defs>
    </svg>
  )
}

/** 模型占位图标 */
export const BoxGlyph = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path
      d="M8 1.5 14 4.75v6.5L8 14.5 2 11.25v-6.5L8 1.5ZM2 4.75 8 8m0 0 6-3.25M8 8v6.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckGlyph = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className={className}>
    <path d="M16.6666 5 7.49992 14.1667 3.33325 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * 模型行列表：图标 / 名称 + 免费角标 / 两行介绍 / 选中勾。
 * 快速生成的模型面板与创作偏好条（agent / 短剧）的模型 chip 共用。
 */
export function ModelOptionList({
  options,
  selectedKey,
  onSelect,
}: {
  options: ModelOption[]
  selectedKey?: string
  onSelect: (key: string) => void
}) {
  const t = useTranslations("generatePage")
  return (
    <div className="flex max-h-[340px] flex-col gap-1 overflow-y-auto">
      {options.map((model) => {
        const selected = model.key === selectedKey
        return (
          <button
            key={model.key}
            type="button"
            onClick={() => onSelect(model.key)}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
              selected ? "bg-card-chip-bg" : "hover:bg-create-card",
            )}
          >
            {/* 模型图标为白色透明底：固定深色底保证明暗两种主题下都有对比度，
                边框随主题（暗色下与面板底分界）；logo 类图标用 contain + 留白不裁切 */}
            <span className="grid size-10 shrink-0 place-content-center overflow-hidden rounded-lg border border-theme-primary/10 bg-[#20222B] text-white/60">
              {model.icon ? (
                <img src={model.icon} alt="" className="size-10 object-contain p-1.5" />
              ) : (
                <BoxGlyph className="size-5" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-theme-primary">
                <span className="truncate">{model.name}</span>
                {model.isFire && <FireGlyph className="size-4 shrink-0" />}
                <OffBadge off={model.off} />
                {model.free > 0 && (
                  <span className="shrink-0 rounded bg-[linear-gradient(106deg,#FEFFD2_3%,#DCFFB6_50%,#4EF1AD_99%)] px-1 text-[10px] font-medium capitalize leading-4 text-[#0E2E1C]">
                    {t("nFree", { count: model.free })}
                  </span>
                )}
              </span>
              {/* 介绍固定展示两行 */}
              <span className="mt-0.5 line-clamp-2 text-xs leading-4 text-theme-primary/40">{model.desc}</span>
            </span>
            {selected && <CheckGlyph className="size-4 shrink-0 text-purple-blue" />}
          </button>
        )
      })}
    </div>
  )
}
